import React from "react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.16, 1, 0.3, 1], delay: i * 0.07 },
  }),
};

const VARIANTS = {
  light: {
    section: "bg-[#fbd9e5]",
    blobPrimary: "bg-white/40",
    blobSecondary: null,
    eyebrow: "text-[#3e2723]/60",
    title: "text-[#3e2723]",
    description: "text-[#3e2723]/75",
    meta: "text-[#3e2723]/60",
    badgeBorder: "rgba(62,39,35,0.15)",
    badgeBg: "rgba(255,255,255,0.5)",
    badgeText: "text-[#3e2723]",
    badgeIcon: "text-[#3e2723]",
  },
  dark: {
    section: "bg-[#3e2723]",
    blobPrimary: "bg-[#fbd9e5]/15",
    blobSecondary: "bg-[#fbd9e5]/10",
    eyebrow: "text-[#fbd9e5]/70",
    title: "text-[#fbd9e5]",
    description: "text-[#fbd9e5]/80",
    meta: "text-[#fbd9e5]/65",
    badgeBorder: "rgba(251,217,229,0.25)",
    badgeBg: "rgba(251,217,229,0.08)",
    badgeText: "text-[#fbd9e5]",
    badgeIcon: "text-[#fbd9e5]",
  },
  plain: {
    section: "bg-white",
    blobPrimary: null,
    blobSecondary: null,
    eyebrow: "text-[#3e2723]/60",
    title: "text-[#3e2723]",
    description: "text-[#7a5c55]",
    meta: "text-[#3e2723]/60",
    badgeBorder: "rgba(62,39,35,0.15)",
    badgeBg: "rgba(255,255,255,0.5)",
    badgeText: "text-[#3e2723]",
    badgeIcon: "text-[#3e2723]",
  },
};

const SIZE_PRESETS = {
  default: {
    section: "min-h-[26rem] flex-col justify-end pt-40 pb-24 sm:min-h-[30rem] sm:pt-52 sm:pb-28",
    title:
      "mt-5 max-w-4xl text-4xl leading-[0.98] sm:mt-6 sm:text-5xl lg:text-6xl xl:text-7xl",
  },
  legal: {
    section: "min-h-0 flex-col justify-end pt-36 pb-14 sm:pt-44 sm:pb-16",
    title:
      "mt-4 max-w-none text-[clamp(0.9375rem,4.1vw,2.125rem)] leading-[1.12] whitespace-nowrap sm:mt-5",
  },
};

/**
 * Unified hero for internal pages (not homepage).
 * Fixed min-height + consistent type scale across About, Services, Portfolio, Contact, Legal, Service detail.
 */
export default function PageHero({
  variant = "light",
  size = "default",
  eyebrow,
  title,
  description,
  meta,
  badgeIcon: BadgeIcon,
  containerClassName = "max-w-7xl",
  testId,
}) {
  const v = VARIANTS[variant] || VARIANTS.light;
  const sizePreset = SIZE_PRESETS[size] || SIZE_PRESETS.default;

  return (
    <section
      className={`relative flex overflow-hidden ${sizePreset.section} ${v.section}`}
      data-testid={testId}
    >
      {v.blobPrimary ? (
        <div
          aria-hidden
          className={`pointer-events-none absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full blur-3xl ${v.blobPrimary}`}
        />
      ) : null}
      {v.blobSecondary ? (
        <div
          aria-hidden
          className={`pointer-events-none absolute bottom-0 -left-40 h-[400px] w-[400px] rounded-full blur-3xl ${v.blobSecondary}`}
        />
      ) : null}

      <div className={`relative mx-auto w-full px-6 sm:px-12 lg:px-20 ${containerClassName}`}>
        {BadgeIcon && eyebrow ? (
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="inline-flex items-center gap-2 rounded-full border px-4 py-2"
            style={{ borderColor: v.badgeBorder, background: v.badgeBg }}
          >
            <BadgeIcon className={`h-3.5 w-3.5 ${v.badgeIcon}`} aria-hidden />
            <span className={`text-[11px] uppercase tracking-[0.25em] ${v.badgeText}`}>{eyebrow}</span>
          </motion.div>
        ) : eyebrow ? (
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className={`text-xs font-bold uppercase tracking-[0.3em] sm:text-sm ${v.eyebrow}`}
          >
            {eyebrow}
          </motion.p>
        ) : null}

        <motion.h1
          variants={fadeUp}
          custom={1}
          initial="hidden"
          animate="visible"
          className={`font-display font-black tracking-tighter ${sizePreset.title} ${v.title}`}
        >
          {title}
        </motion.h1>

        {description ? (
          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="visible"
            className={`mt-6 max-w-2xl text-base leading-relaxed sm:text-lg ${v.description}`}
          >
            {description}
          </motion.p>
        ) : null}

        {meta ? (
          <motion.p
            variants={fadeUp}
            custom={description ? 3 : 2}
            initial="hidden"
            animate="visible"
            className={`mt-4 text-sm ${v.meta}`}
          >
            {meta}
          </motion.p>
        ) : null}
      </div>
    </section>
  );
}
